/**
 * ModelPicker — the vendor-grouped model selector (ADR-182 D2/D5).
 *
 * ONE component for both surfaces that pick a model (AgentProfileFacet on edit,
 * NewAgentArea on create). They previously each mapped over a hand-synced
 * `MODELS` array; two copies of a picker is how one of them ends up offering a
 * vendor the other refuses.
 *
 * WHAT THIS RENDERS THAT A PLAIN <select> COULD NOT
 * -------------------------------------------------
 * A model is a (provider, model) PAIR, so the control returns both. And a
 * vendor whose credential this fleet does not hold must be visibly
 * UNAVAILABLE with the reason and the link — not silently absent, and not
 * selectable. ADR-182 D5: offering it means the operator's change fails at
 * deploy, where `op inject` refuses and takes every profile on the fleet with
 * it.
 *
 * `selectable` IS THE BACKEND'S VERDICT, NOT OURS
 * -----------------------------------------------
 * The disabled state comes straight from the response. Re-deriving it here from
 * `credential` would let the UI and the propose endpoint disagree about what may
 * be proposed, and the UI would be the one that is wrong — it cannot see the
 * catalog the validator uses.
 *
 * `missing` AND `unknown` READ DIFFERENTLY, DELIBERATELY
 * -----------------------------------------------------
 * `missing` = the vault answered and the item is not there → blocked, with the
 * Console link. `unknown` = nobody could look → still selectable, with a
 * caution. Blocking on `unknown` would mean a console hiccup takes every vendor
 * off the menu exactly when someone is trying to diagnose something.
 */
import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { CatalogModel, ModelCatalogResponse, ModelProvider } from '../api/client'

export type ModelChoice = { model: string; provider: string }

function em(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** Load the catalog once per mount. Exposed so a surface can show its own
 *  loading/error affordance rather than this component inventing one. */
export function useModelCatalog() {
  const [data, setData] = useState<ModelCatalogResponse | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    // WRAPPED, BECAUSE A CATALOG READ MUST NOT BE ABLE TO TAKE THE PAGE DOWN.
    //
    // `.catch` only covers a REJECTED promise. If api.getModelCatalog is absent
    // or throws synchronously, the exception escapes the effect and unmounts the
    // whole surface — a model picker's optional read killing the editor it sits
    // in. That is the same failure class as the PR page dying on one GitHub
    // read, and the remedy is the same: an unreadable catalog is a STATE.
    //
    // Observed while wiring the authoring assistant: 22 test files mock `api`
    // partially, so the method was undefined and 219 tests failed on a
    // TypeError rather than on anything they assert. The tests were the messenger
    // — a deployment served an older bundle, or any future refactor that renames
    // the method, would have done the same to an operator mid-edit.
    try {
      const p = api.getModelCatalog?.()
      if (!p) {
        setErr('the model catalog could not be read on this client')
        setLoading(false)
        return () => { cancelled = true }
      }
      p
        .then(d => { if (!cancelled) { setData(d); setErr(null) } })
        .catch(e => { if (!cancelled) setErr(em(e)) })
        .finally(() => { if (!cancelled) setLoading(false) })
    } catch (e) {
      setErr(em(e))
      setLoading(false)
    }
    return () => { cancelled = true }
  }, [])

  return { catalog: data, catalogError: err, catalogLoading: loading }
}

/** The label a legacy pin gets when the catalog no longer lists it. Kept
 *  visible rather than silently reset: a profile running an unlisted model is a
 *  fact an operator needs to see, not one to overwrite on their behalf. */
function unlistedLabel(model: string): string {
  return `${model} (current — not in the catalog)`
}

export function providerOf(
  catalog: ModelCatalogResponse | null, model: string,
): string | undefined {
  return catalog?.providers.find(p => p.models.some(m => m.id === model))?.provider
}

function credentialNote(p: ModelProvider): { text: string; tone: string } | null {
  if (p.credential === 'missing') {
    return {
      text: p.credential_detail || 'credential not provisioned for this fleet',
      tone: 'text-red-700',
    }
  }
  if (p.credential === 'unknown') {
    return {
      text: p.credential_detail || 'credential status could not be checked',
      tone: 'text-amber-700',
    }
  }
  return null
}

export function ModelPicker({
  value,
  onChange,
  catalog,
  loading,
  disabled,
  currentUnlisted,
  id,
  gate = 'selectable',
  defaultOptionLabel,
  defaultOptionNamesCatalogDefault,
}: {
  value: ModelChoice
  onChange: (next: ModelChoice) => void
  catalog: ModelCatalogResponse | null
  loading?: boolean
  disabled?: boolean
  /** A model the profile is pinned to that the catalog does not list. */
  currentUnlisted?: string
  id?: string
  /** WHICH AVAILABILITY FACT TO GATE ON (ADR-183 Phase 6). Defaults to
   *  `selectable`, so the agent-profile caller is unchanged.
   *
   *  `selectable` — may this vendor be PROPOSED for an agent's profile. An
   *    agent runs on its own host with its own injected key, so a vendor
   *    Mission Control cannot call is still a legitimate choice there.
   *  `mc_callable` — can MISSION CONTROL ITSELF place the call. The right gate
   *    for the authoring assistant, because that call is placed by MC. Gating
   *    the assistant on `selectable` would offer a vendor whose key is in
   *    1Password but not in this container, and the turn would fail later with
   *    a 503 naming neither cause.
   *
   *  ONE PICKER WITH A GATE, not two pickers: the option list, the pair
   *  encoding and the "here is why this is unavailable" reporting are identical,
   *  and a second copy would drift on the part that matters — which vendors
   *  appear. */
  gate?: 'selectable' | 'mc_callable'
  /** When set, a leading option meaning "no override" whose value is the empty
   *  pair. The assistant needs it — an operator must be able to go BACK to the
   *  deployment's configured model, and that is what keeps a comparison against
   *  a stable baseline possible. Omitted for the profile facet, where a model is
   *  required. */
  defaultOptionLabel?: string
  /** Like defaultOptionLabel, but the label is DERIVED — "Default — <model>
   *  (<vendor>)" from the catalog's own default_provider/default_model.
   *
   *  WHY THIS EXISTS RATHER THAN A LONGER STRING AT THE CALL SITE (issue #2422).
   *  The assistant passed "Default (this fleet's configured model)", which names
   *  no model — so the control could not be used for the one thing it is for,
   *  comparing two models, without opening another page to find out what the
   *  baseline even is.
   *
   *  The pattern is borrowed from AgentProfileFacet's provenance line, which
   *  states `Currently claude-sonnet-5 — pinned for this fleet in fleet.yaml`
   *  for the same reason that file gives: an operator who cannot tell what is in
   *  force acts defensively on a guess.
   *
   *  It falls back to the generic wording when the catalog has not loaded or
   *  declares no default — an honest "we do not know yet" rather than a name
   *  invented from a constant. */
  defaultOptionNamesCatalogDefault?: boolean
}) {
  // The select's value encodes the PAIR, so two vendors offering the same id
  // can never collapse into one option.
  const key = (provider: string, model: string) => `${provider} ${model}`
  const selected = key(value.provider, value.model)

  // ONE PREDICATE, read from the gate, used for the options AND the reasons —
  // so the list and the explanation underneath it can never disagree about
  // which vendors are usable.
  const usable = (p: ModelProvider) => (gate === 'mc_callable' ? p.mc_callable : p.selectable)

  // The default option's text. NAMES THE MODEL when the catalog says what it is.
  const catalogDefaultLabel = (): string | undefined => {
    if (!defaultOptionNamesCatalogDefault) return defaultOptionLabel
    const dm = catalog?.default_model?.trim()
    if (!dm) return "Default (this fleet's configured model)"
    const dp = catalog?.default_provider?.trim()
    const vendor = catalog?.providers.find(p => p.provider === dp)?.label ?? dp
    return vendor ? `Default — ${dm} (${vendor})` : `Default — ${dm}`
  }
  const defaultLabel = catalogDefaultLabel()
  const blockedReason = (p: ModelProvider) =>
    gate === 'mc_callable' ? p.mc_callable_detail : p.credential_detail
  const blocked = catalog?.providers.filter(p => !usable(p)) ?? []

  return (
    <div className="space-y-1">
      <select
        id={id}
        value={selected}
        disabled={disabled || loading}
        onChange={e => {
          // Split on the FIRST space only. A provider slug never contains
          // one, but a model id is the VENDOR's string, not ours — splitting
          // greedily would silently truncate an id the day one does.
          const raw = e.target.value
          const at = raw.indexOf(' ')
          onChange(at < 0
            ? { provider: raw, model: '' }
            : { provider: raw.slice(0, at), model: raw.slice(at + 1) })
        }}
        className="mt-1 block text-sm border border-gray-300 rounded px-2 py-1 min-w-[16rem] disabled:opacity-50"
      >
        {defaultLabel && (
          /* The empty pair is the "no override" sentinel. Its value is a single
             space so the split-on-first-space decoder yields two empty strings
             rather than a provider named "". */
          <option value=" ">{defaultLabel}</option>
        )}
        {currentUnlisted && (
          <option value={key(value.provider, currentUnlisted)}>
            {unlistedLabel(currentUnlisted)}
          </option>
        )}
        {catalog?.providers.map(p => (
          <optgroup
            key={p.provider}
            /* The vendor's own words plus, when it cannot be used, why — so the
               reason is visible at the point of choosing rather than only after
               a failed propose. */
            label={usable(p) ? p.label : `${p.label} — unavailable`}
          >
            {p.models.length === 0 && (
              <option disabled value={key(p.provider, '')}>
                no models published yet
              </option>
            )}
            {p.models.map((m: CatalogModel) => (
              <option
                key={key(p.provider, m.id)}
                value={key(p.provider, m.id)}
                disabled={!usable(p)}
              >
                {m.label}
                {m.variant ? ' (variant)' : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      {loading && <p className="text-[11px] text-gray-500">Loading the model catalog…</p>}

      {/* Every blocked vendor still gets its reason and its fix — COLLAPSED
          behind one line (issue #2422).
          Each message is individually worth keeping: they distinguish "no key in
          MC's environment" from "key provisioned but MC not redeployed since"
          from "MC does not implement this vendor's API format yet" (and that
          last one correctly notes an AGENT on the fleet can still call it,
          because the agent host places that call, not MC). That nuance was
          hard-won and none of it is removed here.
          But rendered as one red paragraph PER vendor, three unprovisioned
          vendors put a wall of red above the input box of someone who is trying
          to author a workflow — it reads as a page full of errors rather than as
          a menu with three options greyed out. The summary states the fact; the
          detail is one click away for whoever is actually provisioning.
          Open by default when there is exactly ONE, because a single vendor's
          reason is usually the thing the operator just came to read. */}
      {blocked.length > 0 && (
        <details className="text-[11px]" open={blocked.length === 1}>
          <summary className="text-gray-600 cursor-pointer">
            {blocked.length} of {catalog?.providers.length ?? blocked.length} vendors
            unavailable — {blocked.map(p => p.label).join(', ')}
          </summary>
          <div className="mt-0.5 space-y-0.5 pl-3">
            {blocked.map(p => {
              const note = credentialNote(p)
              return (
                <p key={p.provider} className="text-gray-600">
                  <span className="font-medium">{p.label}</span> —{' '}
                  <span className={note?.tone ?? 'text-gray-600'}>
                    {blockedReason(p) ?? note?.text ?? 'no models published yet'}
                  </span>
                  {p.console_path && (
                    <>
                      {' '}
                      <a className="text-indigo-600 underline" href={p.console_path}
                         target="_blank" rel="noreferrer">provision it in Console</a>
                    </>
                  )}
                </p>
              )
            })}
          </div>
        </details>
      )}

      {/* Said once, not per vendor: the difference between "no vendor has a key"
          and "we could not ask" is the difference between a provisioning task
          and an outage. */}
      {catalog?.credential_check === 'unavailable' && (
        <p className="text-[11px] text-amber-700">
          Credential status could not be checked, so vendor availability below is
          unverified — a selection may still fail at deploy.
        </p>
      )}
    </div>
  )
}
